import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from '../../../store'
import { setPolicyError, setPolicyLoading, setPolicyReady } from './actions'
import {
  canDo,
  gatekeeperPolicyEngine,
  type GatekeeperPolicyAssets,
} from './policyEngine'
import {
  isAppScope,
  type AccessLevel,
  type AppScope,
  type Cloud,
  type LabraService,
} from '../../utils/authorizationModel'

export type AuthResource = {
  cloud?: Cloud
  service?: LabraService
  action?: AccessLevel
}

export type CanDoFn = (resource: AuthResource) => boolean

/** Injected policy loader (defaults to the app singleton). */
export type AuthPolicyEngine = {
  preloadPolicy: () => Promise<GatekeeperPolicyAssets>
  isPolicyLoaded: () => boolean
  getCachedPolicy: () => GatekeeperPolicyAssets | null
}

export type UseAuthResult = {
  /**
   * Permission check against loaded policy. When `loading` is true, results are
   * deny-by-default; gate UI on `loading` if you need to avoid flicker.
   */
  canDo: CanDoFn
  loading: boolean
  setScopes: (xs: string[]) => void
  policyError: string | null
}

const AuthPolicyContext = createContext<UseAuthResult | null>(null)

type AuthPolicyProviderProps = {
  children: React.ReactNode
  /** Policy WASM bundle loader; inject for tests. */
  policyEngine?: AuthPolicyEngine
}

export const AuthPolicyProvider: React.FC<AuthPolicyProviderProps> = ({
  children,
  policyEngine = gatekeeperPolicyEngine,
}) => {
  const dispatch = useDispatch()
  const [scopes, setScopesState] = useState<AppScope[]>([])
  const [policy, setPolicy] = useState<GatekeeperPolicyAssets | null>(() =>
    policyEngine.getCachedPolicy()
  )
  const [policyLoadStatus, setPolicyLoadStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >(() =>
    policyEngine.isPolicyLoaded() && policyEngine.getCachedPolicy()
      ? 'ready'
      : 'idle'
  )
  const [policyLoadError, setPolicyLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const syncReady = (assets: GatekeeperPolicyAssets) => {
      dispatch(setPolicyReady())
      setPolicyLoadStatus('ready')
      setPolicyLoadError(null)
      if (!cancelled) {
        setPolicy(assets)
      }
    }

    const syncError = (message: string) => {
      console.warn(`Policy failed to load: ${message}`)
      dispatch(setPolicyError(message))
      setPolicyLoadStatus('error')
      setPolicyLoadError(message)
      if (!cancelled) {
        setPolicy(null)
      }
    }

    if (policyEngine.isPolicyLoaded()) {
      const assets = policyEngine.getCachedPolicy()
      if (assets) {
        syncReady(assets)
        return () => {
          cancelled = true
        }
      }
    }

    dispatch(setPolicyLoading())
    setPolicyLoadStatus('loading')
    policyEngine
      .preloadPolicy()
      .then(assets => {
        syncReady(assets)
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Policy preload failed'
        syncError(message)
      })
    return () => {
      cancelled = true
    }
  }, [policyEngine])

  const loading = useMemo(
    () =>
      policyLoadStatus === 'loading' ||
      (policyLoadStatus === 'idle' && !policyEngine.isPolicyLoaded()),
    [policyLoadStatus, policyEngine]
  )

  const setScopes = useCallback((xs: string[]) => {
    setScopesState(xs.filter(isAppScope))
  }, [])

  const canDoBound = useCallback(
    (resource: AuthResource): boolean => {
      if (policyLoadError) {
        return false
      }
      const { cloud, service, action } = resource
      if (!cloud || !service || !action) {
        return false
      }
      return canDo(policy, scopes, { cloud, service }, action)
    },
    [policy, scopes, policyLoadError]
  )

  const value = useMemo(
    () => ({
      canDo: canDoBound,
      loading,
      setScopes,
      policyError: policyLoadError,
    }),
    [canDoBound, loading, setScopes, policyLoadError]
  )

  return (
    <AuthPolicyContext.Provider value={value}>
      {children}
    </AuthPolicyContext.Provider>
  )
}

export const useAuth = (): UseAuthResult => {
  const ctx = useContext(AuthPolicyContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthPolicyProvider')
  }
  return ctx
}
